import { Suspense, lazy } from "react";
import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import TrackingScripts from "@/components/TrackingScripts";
import LandingPage from "@/pages/LandingPage";
import SearchResults from "@/pages/SearchResults";
import Checkout from "@/pages/Checkout";
import Payment from "@/pages/Payment";
import OrderSuccess from "@/pages/OrderSuccess";
import VerifyOrder from "@/pages/VerifyOrder";
import MyOrders from "@/pages/MyOrders";
import CountryGuide from "@/pages/CountryGuide";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import NotFound from "@/pages/NotFound";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Toaster } from "@/components/ui/sonner";

// Code-split admin pages
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminPricing = lazy(() => import("@/pages/admin/AdminPricing"));
const AdminPromoCodes = lazy(() => import("@/pages/admin/AdminPromoCodes"));
const AdminTestimonials = lazy(() => import("@/pages/admin/AdminTestimonials"));
const AdminFAQs = lazy(() => import("@/pages/admin/AdminFAQs"));
const AdminCountries = lazy(() => import("@/pages/admin/AdminCountries"));
const AdminNewsletter = lazy(() => import("@/pages/admin/AdminNewsletter"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
              <p className="text-[#6b7280] font-['Plus_Jakarta_Sans']">Loading...</p>
            </div>
          </div>
        }>
          <BrowserRouter>
            <TrackingScripts />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/search-results" element={<SearchResults />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/payment" element={<Payment />} />
              <Route path="/checkout/success/:bookingRef" element={<OrderSuccess />} />
              <Route path="/verify/:bookingRef" element={<VerifyOrder />} />
              <Route path="/my-orders" element={<MyOrders />} />
              <Route path="/guides/:slug" element={<CountryGuide />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="pricing" element={<AdminPricing />} />
                <Route path="promo-codes" element={<AdminPromoCodes />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="faqs" element={<AdminFAQs />} />
                <Route path="countries" element={<AdminCountries />} />
                <Route path="newsletter" element={<AdminNewsletter />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
            <WhatsAppButton />
            <Toaster position="top-right" />
          </BrowserRouter>
        </Suspense>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
